
import React, { useEffect, useState, useRef } from 'react';
import { useIsMobile } from '../../hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

const OurStorySection: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const isMobile = useIsMobile();
  const lineRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const { language } = useLanguage();

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const sectionTop = sectionRef.current?.getBoundingClientRect().top || 0;
          const sectionBottom = sectionRef.current?.getBoundingClientRect().bottom || 0;
          
          // Check if the section is in view
          if (sectionTop <= 100 && sectionBottom >= 300) {
            setIsScrolled(true);
          } else {
            setIsScrolled(false);
          }

          if (lineRef.current && sectionRef.current) {
            const sectionHeight = sectionRef.current.offsetHeight;
            const sectionOffset = sectionRef.current.offsetTop;
            const windowHeight = window.innerHeight;
            const scrollPosition = window.scrollY - sectionOffset + windowHeight / 2;
            
            const scrollPercentage = Math.min(Math.max(scrollPosition / sectionHeight * 1.2, 0), 1);
            
            lineRef.current.style.height = `${scrollPercentage * 100}%`;
          }
          
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const content = {
    titleTop: language === 'en' ? "Our" : "Nuestra",
    titleBottom: language === 'en' ? "story" : "historia",
    paragraphs: language === 'en' ? [
      "Our founders established Noa Partners with a clear vision: to create a lasting home for exceptional software companies and their teams. Inspired by the story of Noah's Ark—preserving and nurturing what is valuable for the future—we are dedicated to safeguarding generational businesses while guiding them toward long-term success.",
      "With deep expertise in technology investment and entrepreneurship, we saw a gap in the market for a truly founder-friendly acquisition partner—one that prioritizes legacy, sustainable growth, and long-term value creation.",
      "Frustrated by the traditional venture capital and private equity models that focus on quick exits and unsustainable growth, we set out to build a better alternative. Noa Partners invests with patient capital, ensuring each company retains its unique culture and vision while gaining access to the resources needed for sustainable expansion.",
      "Today, we partner with B2B software businesses across Latin America, fostering an environment where innovation thrives without the pressure of artificial timelines or forced exits. Our decentralized model empowers teams to maintain their entrepreneurial spirit while benefiting from the strength of our broader ecosystem."
    ] : [
      "Nuestros fundadores establecieron Noa Partners con una visión clara: crear un hogar duradero para compañías de software excepcionales y sus equipos. Inspirados por la historia del Arca de Noé—preservando y nutriendo lo que es valioso para el futuro—estamos dedicados a salvaguardar negocios generacionales mientras los guiamos hacia el éxito a largo plazo.",
      "Con profunda experiencia en inversión tecnológica y emprendimiento, vimos una brecha en el mercado para un socio de adquisición verdaderamente amigable con los fundadores—uno que prioriza el legado, el crecimiento sostenible y la creación de valor a largo plazo.",
      "Frustrados por los modelos tradicionales de capital de riesgo y capital privado que se centran en salidas rápidas y crecimiento insostenible, nos propusimos construir una mejor alternativa. Noa Partners invierte con capital paciente, asegurando que cada empresa conserve su cultura y visión únicas mientras obtiene acceso a los recursos necesarios para una expansión sostenible.",
      "Hoy, nos asociamos con empresas de software B2B en toda América Latina, fomentando un entorno donde la innovación prospera sin la presión de plazos artificiales o salidas forzadas. Nuestro modelo descentralizado permite a los equipos mantener su espíritu emprendedor mientras se benefician de la fortaleza de nuestro ecosistema más amplio."
    ]
  };

  return (
    <section id="our-story-section" ref={sectionRef} className="py-24 bg-white dark:bg-noa-rich-black relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Title column - takes 5 columns on large screens */}
          <div className={`lg:col-span-5 ${!isMobile && isScrolled ? 'lg:sticky lg:top-24' : ''}`}>
            <div className="sticky top-32">
              <h2 className="font-space-grotesk text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-noa-rich-black dark:text-noa-antiflash-white">
                {content.titleTop}
              </h2>
              <h2 className="font-space-grotesk text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-noa-rich-black dark:text-noa-antiflash-white">
                {content.titleBottom}<span className="text-blue-600 dark:text-blue-400">.</span>
              </h2>
            </div>
          </div>
          
          {/* Content column - takes 7 columns on large screens */}
          <div className="lg:col-span-7 relative">
            {/* Add the vertical line with animation */}
            <div className="absolute left-0 w-0.5 bg-noa-charcoal/20 dark:bg-noa-antiflash-white/20 h-full z-0">
              <div 
                ref={lineRef} 
                className="absolute top-0 left-0 w-full bg-gradient-to-b from-blue-600 via-blue-500 to-blue-400 dark:from-blue-500 dark:via-blue-400 dark:to-blue-300 rounded-full" 
                style={{ height: '0%' }}
              />
            </div>

            <div className="space-y-8 pl-8">
              <p className="text-lg sm:text-xl text-noa-charcoal dark:text-noa-antiflash-white/90 leading-relaxed">
                {content.paragraphs[0]}
              </p>
              
              {/* Increased vertical spacing around the image - approximately 1cm space (40px) */}
              <div className="mt-32 mb-32 relative">
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src="/lovable-uploads/54716209-f887-41ec-9bf0-ca30ffb31dbd.png" 
                    alt={language === 'en' ? "Noa Partners founders" : "Fundadores de Noa Partners"} 
                    className="w-full object-cover transition-transform hover:scale-105 duration-500"
                  />
                </div>
                {/* Add decorative elements similar to WhatWeDoSection */}
                <div className="hidden md:block w-64 h-64 bg-[#2F4550]/10 dark:bg-[#403E43]/10 rounded-full absolute -bottom-20 -right-20 z-0"></div>
                <div className="hidden md:block w-32 h-32 bg-[#586F7C]/10 dark:bg-[#0EA5E9]/10 rounded-full absolute top-1/3 -left-16 z-0"></div>
              </div>
              
              <p className="text-lg sm:text-xl text-noa-charcoal dark:text-noa-antiflash-white/90 leading-relaxed">
                {content.paragraphs[1]}
              </p>
              
              <p className="text-lg sm:text-xl text-noa-charcoal dark:text-noa-antiflash-white/90 leading-relaxed">
                {content.paragraphs[2]}
              </p>

              <p className="text-lg sm:text-xl text-noa-charcoal dark:text-noa-antiflash-white/90 leading-relaxed">
                {content.paragraphs[3]}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurStorySection;
